import { BezierGrid } from './BezierGrid';
import { BezierGridRenderer } from './BezierGridRenderer';
import { Point2D } from './BezierMath';
import { ViewportController } from '../visualization/ViewportController';

const HIT_RADIUS = 12;

export class BezierControlPointEditor {
  private grid: BezierGrid;
  private renderer: BezierGridRenderer;
  private canvas: HTMLCanvasElement;
  private viewportController?: ViewportController;
  private dragging: Point2D | null = null;
  private enabled: boolean = false;

  private boundMouseDown: (e: MouseEvent) => void;
  private boundMouseMove: (e: MouseEvent) => void;
  private boundMouseUp: (e: MouseEvent) => void;

  constructor(
    grid: BezierGrid,
    renderer: BezierGridRenderer,
    viewportController?: ViewportController
  ) {
    this.grid = grid;
    this.renderer = renderer;
    this.canvas = renderer.getCanvas();
    this.viewportController = viewportController;

    this.boundMouseDown = this.handleMouseDown.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);

    this.canvas.addEventListener('mousedown', this.boundMouseDown, true);
    this.canvas.addEventListener('mousemove', this.boundMouseMove, true);
    this.canvas.addEventListener('mouseup', this.boundMouseUp, true);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.dragging = null;
      this.canvas.style.cursor = '';
    }
  }

  private toCanvasCoords(clientX: number, clientY: number): { x: number; y: number } {
    if (this.viewportController) {
      return this.viewportController.screenToCanvas(clientX, clientY);
    }
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  private findPointAt(x: number, y: number): Point2D | null {
    const points = this.grid.getAllDraggablePoints();
    let bestDist = HIT_RADIUS;
    let best: Point2D | null = null;

    for (const { point } of points) {
      const dx = point.x - x;
      const dy = point.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < bestDist) {
        bestDist = dist;
        best = point;
      }
    }

    return best;
  }

  private handleMouseDown(e: MouseEvent): void {
    if (!this.enabled || e.button !== 0) return;

    const { x, y } = this.toCanvasCoords(e.clientX, e.clientY);
    const hit = this.findPointAt(x, y);

    if (hit) {
      e.stopImmediatePropagation();
      e.preventDefault();
      this.dragging = hit;
      this.canvas.style.cursor = 'grabbing';
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.enabled) return;

    const { x, y } = this.toCanvasCoords(e.clientX, e.clientY);

    if (this.dragging) {
      e.stopImmediatePropagation();
      e.preventDefault();

      this.dragging.x = x;
      this.dragging.y = y;

      this.grid.recomputeVertices();
      this.renderer.render();
      return;
    }

    const hit = this.findPointAt(x, y);
    this.canvas.style.cursor = hit ? 'grab' : '';
  }

  private handleMouseUp(e: MouseEvent): void {
    if (!this.enabled) return;

    if (this.dragging) {
      e.stopImmediatePropagation();
      this.dragging = null;
      const { x, y } = this.toCanvasCoords(e.clientX, e.clientY);
      const hit = this.findPointAt(x, y);
      this.canvas.style.cursor = hit ? 'grab' : '';
    }
  }

  destroy(): void {
    this.canvas.removeEventListener('mousedown', this.boundMouseDown, true);
    this.canvas.removeEventListener('mousemove', this.boundMouseMove, true);
    this.canvas.removeEventListener('mouseup', this.boundMouseUp, true);
  }
}
